import { Link } from 'react-router-dom';

import {
  getConsoleRouteForPath,
  getConsoleRouteGroupLabel,
  type ConsoleRouteContext,
  type getVisibleConsoleSections,
} from '@/navigation/consoleNavigation';
import { cx, focusRingClass, interactiveClass } from '@/components/ui/shared';

import ConsoleNavigationMenu from './ConsoleNavigationMenu';

type ConsoleSections = ReturnType<typeof getVisibleConsoleSections>;

interface ConsoleNavigationPanelProps {
  context: ConsoleRouteContext;
  currentPath: string;
  sections: ConsoleSections;
  onNavigate?: () => void;
  /**
   * `bar`: one row in the top bar; a group with a menu name becomes one menu.
   * `list`: grouped list for the phone drawer.
   */
  variant: 'bar' | 'list';
}

export default function ConsoleNavigationPanel({
  context,
  currentPath,
  sections,
  onNavigate,
  variant,
}: ConsoleNavigationPanelProps) {
  const activeRoute = getConsoleRouteForPath(currentPath);
  const roleNote = (
    <span className="sr-only">
      Navigation is filtered for {context.userRole ?? 'the active account'}.
    </span>
  );

  if (variant === 'bar') {
    // Menus sit outside the scrolling row so their open list is not clipped.
    const menuSections = sections.filter((section) => section.menuLabel);
    const routes = sections
      .filter((section) => !section.menuLabel)
      .flatMap((section) => section.routes);
    return (
      <nav aria-label="Console tasks" className="flex min-w-0 items-center gap-1">
        {menuSections.map((section) => (
          <ConsoleNavigationMenu
            key={section.id}
            label={getConsoleRouteGroupLabel(section)}
            routes={section.routes}
            activeRouteId={activeRoute?.id ?? null}
            onNavigate={onNavigate}
          />
        ))}
        {routes.length > 0 ? (
          <div className="min-w-0 overflow-x-auto">
            <ul className="flex w-max items-center gap-1">
              {routes.map((route) => {
                const isActive = activeRoute?.id === route.id;
                return (
                  <li key={route.id}>
                    <Link
                      to={route.path}
                      onClick={onNavigate}
                      aria-current={isActive ? 'page' : undefined}
                      data-so-selected={isActive || undefined}
                      className={cx(
                        focusRingClass,
                        interactiveClass,
                        'flex min-h-10 items-center rounded-control px-3 text-control font-semibold whitespace-nowrap',
                        isActive
                          ? 'bg-surface-raised text-ink-strong shadow-inset'
                          : 'text-ink-muted hover:bg-surface hover:text-ink',
                      )}
                    >
                      {route.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {roleNote}
      </nav>
    );
  }

  return (
    <nav aria-label="Console tasks" className="grid gap-6">
      {sections.map((section) => (
        <section key={section.id} aria-labelledby={`console-domain-${section.id}`}>
          <h2
            id={`console-domain-${section.id}`}
            className="px-3 text-meta font-semibold text-ink-muted"
          >
            {getConsoleRouteGroupLabel(section)}
          </h2>
          <div className="mt-2 grid gap-1">
            {section.routes.map((route) => {
              const Icon = route.icon;
              const isActive = activeRoute?.id === route.id;
              return (
                <Link
                  key={route.id}
                  to={route.path}
                  onClick={onNavigate}
                  aria-current={isActive ? 'page' : undefined}
                  data-so-selected={isActive || undefined}
                  className={cx(
                    focusRingClass,
                    interactiveClass,
                    'flex min-h-control-comfortable items-center gap-3 rounded-control px-3 text-control font-semibold',
                    isActive
                      ? 'bg-accent-soft text-accent-ink'
                      : 'text-ink hover:bg-surface-recessed',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {route.label}
                </Link>
              );
            })}
          </div>
        </section>
      ))}
      {roleNote}
    </nav>
  );
}
